import { useEffect, useRef } from "react";
import { useFetcher } from "react-router-dom";
import type { RecommendationsLoaderData } from "../../data/recommendations/recommendationsDataLoader.ts";
import { supportsRecommendations as supportsRecommendationsFn } from "./useSupportsRecommendations.ts";
import type { DeweySchema } from "../../../root/helpers/brandSchema.ts";
import type { User } from "../../../../common/api/decimal/methods/user.ts";

const isValidDate = (date?: Date): date is Date =>
  !!(date && !isNaN(date.getTime()));

export function useRecommendationsNotify(
  lastSeenRecommendations: string | undefined,
  brand: DeweySchema,
  user: User
) {
  const fetcher = useFetcher<RecommendationsLoaderData>();
  const lastCheck = useRef(lastSeenRecommendations);
  const supportsRecommendations = supportsRecommendationsFn(brand, user);

  useEffect(() => {
    if (
      supportsRecommendations &&
      ((fetcher.state === "idle" && !fetcher.data) ||
        lastCheck.current !== lastSeenRecommendations)
    ) {
      const lastSeenRecommendationsDate = lastSeenRecommendations
        ? new Date(lastSeenRecommendations)
        : undefined;

      const params = new URLSearchParams();
      if (isValidDate(lastSeenRecommendationsDate)) {
        params.set("createdAt", lastSeenRecommendationsDate.toISOString());
        params.set("cmp", "gt");
      }
      void fetcher.load(`/data/recommendations?${params.toString()}`);
      lastCheck.current = lastSeenRecommendations;
    }
  }, [fetcher, lastSeenRecommendations, supportsRecommendations]);

  const unseenRecommendations =
    fetcher.data?.ok && Array.isArray(fetcher.data.data)
      ? fetcher.data.data
      : [];

  return {
    unseenRecommendationsCount: unseenRecommendations.length,
  };
}
